/**
 * Export Dialog component for quick data exports
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Chip,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { Download as DownloadIcon } from '@mui/icons-material';

import { analyticsService, ExportRequest } from '../../services/analyticsService';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  defaultExportType?: string;
  title?: string;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  defaultExportType = 'analytics',
  title = 'Exportar Datos'
}) => {
  const [exportType, setExportType] = useState(defaultExportType);
  const [format, setFormat] = useState('csv');
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [projectCodes, setProjectCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportTypes = [
    { value: 'analytics', label: 'Datos de Analytics' },
    { value: 'conversations', label: 'Conversaciones' },
    { value: 'leads', label: 'Leads' },
    { value: 'performance', label: 'Métricas de Rendimiento' }
  ];

  const formats = [
    { value: 'csv', label: 'CSV', description: 'Archivo de valores separados por comas' },
    { value: 'excel', label: 'Excel', description: 'Libro de Excel con múltiples hojas' },
    { value: 'json', label: 'JSON', description: 'Formato JSON estructurado' }
  ];

  const handleExport = async () => {
    if (!startDate || !endDate) {
      setError('Por favor selecciona las fechas de inicio y fin');
      return;
    }

    if (startDate > endDate) {
      setError('La fecha de inicio debe ser anterior a la fecha de fin');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const exportRequest: ExportRequest = {
        export_type: exportType,
        filters: {
          date_range: {
            start_date: startDate.toISOString().split('T')[0],
            end_date: endDate.toISOString().split('T')[0]
          },
          project_codes: projectCodes.length > 0 ? projectCodes : undefined
        },
        format,
        include_metadata: includeMetadata
      };

      const data = await analyticsService.exportData(exportRequest);

      // Create download link
      const blob = new Blob([data], {
        type: getContentType(format)
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getFileName(exportType, format, startDate, endDate);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onClose();
    } catch (err) {
      setError('Error al exportar los datos. Por favor intenta nuevamente.');
      console.error('Export error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getContentType = (format: string): string => {
    switch (format) {
      case 'csv':
        return 'text/csv';
      case 'excel':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'json':
        return 'application/json';
      default:
        return 'application/octet-stream';
    }
  };

  const getFileName = (type: string, format: string, start: Date, end: Date): string => {
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    return `${type}_${startStr}_${endStr}.${format}`;
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const getExportDescription = (type: string): string => {
    switch (type) {
      case 'analytics':
        return 'Exporta métricas del dashboard, leads y rendimiento del sistema';
      case 'conversations':
        return 'Exporta datos detallados de conversaciones con usuarios';
      case 'leads':
        return 'Exporta información de leads generados y su estado';
      case 'performance':
        return 'Exporta métricas de rendimiento del sistema y bot';
      default:
        return '';
    }
  };

  const getEstimatedSize = (): string => {
    if (!startDate || !endDate) return '';
    
    const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    let estimatedRows = 0;

    switch (exportType) {
      case 'analytics':
        estimatedRows = days * 3; // Dashboard, leads, performance metrics per day
        break;
      case 'conversations':
        estimatedRows = days * 50; // Estimated conversations per day
        break;
      case 'leads':
        estimatedRows = days * 20; // Estimated leads per day
        break;
      case 'performance':
        estimatedRows = days; // One row per day
        break;
    }

    if (estimatedRows < 100) return 'Pequeño (< 1 MB)';
    if (estimatedRows < 1000) return 'Mediano (1-5 MB)';
    return 'Grande (> 5 MB)';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Datos</InputLabel>
              <Select
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
                label="Tipo de Datos"
                disabled={loading}
              >
                {exportTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {getExportDescription(exportType)}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha de Inicio"
                value={startDate}
                onChange={setStartDate}
                disabled={loading}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha de Fin"
                value={endDate}
                onChange={setEndDate}
                disabled={loading}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Formato de Archivo</InputLabel>
              <Select
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                label="Formato de Archivo"
                disabled={loading}
              >
                {formats.map((fmt) => (
                  <MenuItem key={fmt.value} value={fmt.value}>
                    <Box>
                      <Typography variant="body1">{fmt.label}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {fmt.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Opciones Adicionales
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Incluir metadatos (fechas de creación, IDs, etc.)"
            />
          </Grid>

          {exportType === 'conversations' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Códigos de Proyecto (opcional)"
                placeholder="Ej: PROJ001, PROJ002"
                value={projectCodes.join(', ')}
                onChange={(e) => setProjectCodes(
                  e.target.value.split(',').map(code => code.trim()).filter(code => code)
                )}
                disabled={loading}
                helperText="Deja vacío para incluir todos los proyectos"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Información del Export
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Período: ${startDate?.toLocaleDateString('es-ES')} - ${endDate?.toLocaleDateString('es-ES')}`}
                  size="small"
                />
                <Chip
                  label={`Formato: ${format.toUpperCase()}`}
                  size="small"
                />
                <Chip
                  label={`Tamaño estimado: ${getEstimatedSize()}`}
                  size="small"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={loading || !startDate || !endDate}
        >
          {loading ? 'Exportando...' : 'Exportar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;